import React, { useEffect, useState } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Spinner,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Form,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import { useParams } from "react-router-dom";
import classnames from "classnames";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import OrganizationService from "../../../services/OrderingServices/OrganizationService";
import NCHLCredentialsService from "../../../services/OrderingServices/NCHLCredentialsService";
import toast from "react-hot-toast";

/* ---------- Small reusable UI helpers ---------- */
const InfoRow = ({ label, value }) => (
  <div className="d-flex mb-2">
    <div className="fw-medium me-2">{label}:</div>
    <div>{value || <span className="text-muted">—</span>}</div>
  </div>
);

const SectionTitle = ({ title }) => (
  <h5 className="mt-4 mb-3 text-primary fw-bold border-bottom pb-2">{title}</h5>
);

/* ---------- Main Component ---------- */
const TenantDetail = () => {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("1");

  // Password visibility states
  const [showAuthPassword, setShowAuthPassword] = useState(false);
  const [showNpiPassword, setShowNpiPassword] = useState(false);
  const [showGatewayAuthPassword, setShowGatewayAuthPassword] = useState(false);
  const [showGatewayCreditorPassword, setShowGatewayCreditorPassword] =
    useState(false);

  // NCHL Dynamic QR States
  const [dynamicQrList, setDynamicQrList] = useState([]);
  const [dynamicQrLoading, setDynamicQrLoading] = useState(false);
  const [showDynamicQrModal, setShowDynamicQrModal] = useState(false);
  const [editingDynamicQr, setEditingDynamicQr] = useState(null);
  const [dynamicQrFormData, setDynamicQrFormData] = useState({
    organizationId: "",
    isUat: true,
    isLive: false,
    initilizationPoint: 0,
    merchantCode: "",
    acquirerId: "",
    merchantName: "",
    merchantCategoryCode: "",
    txnCurrencyCode: "",
    signerBasePath: "",
    signerPath: "",
    authUserName: "",
    basicUserName: "",
    authPassword: "",
    npiPfxPassword: "",
    dynamicQrUrl: "",
    publicKey: "",
    apiKey: "",
    webSocketUsername: "",
    webSocketUrl: "",
  });

  // NCHL Gateway States
  const [gatewayList, setGatewayList] = useState([]);
  const [gatewayLoading, setGatewayLoading] = useState(false);
  const [showGatewayModal, setShowGatewayModal] = useState(false);
  const [editingGateway, setEditingGateway] = useState(null);
  const [gatewayFormData, setGatewayFormData] = useState({
    organizationId: "",
    isUat: true,
    isLive: false,
    merchantId: "",
    appId: "",
    appName: "",
    txnCrncy: "",
    gatewyaBasicAuthPassword: "",
    creditorPfxPassword: "",
    signerExternalLink: "",
    singerPath: "",
    gatewayBaseUrl: "",
  });

  // Set Active Modal States
  const [showSetActiveModal, setShowSetActiveModal] = useState(false);
  const [setActiveType, setSetActiveType] = useState(null); // "dynamicQr" or "gateway"
  const [setActiveItem, setSetActiveItem] = useState(null);

  const fetchData = async () => {
    try {
      const res = await OrganizationService.tenantDetail(id);
      setData(res);
    } catch (error) {
      toast.error("Failed to fetch tenant detail");
    } finally {
      setLoading(false);
    }
  };

  // ============ Set Active Modal Handlers ============
  const openSetActiveModal = (item, type) => {
    setSetActiveItem(item);
    setSetActiveType(type);
    setShowSetActiveModal(true);
  };

  const closeSetActiveModal = () => {
    setShowSetActiveModal(false);
    setSetActiveItem(null);
    setSetActiveType(null);
  };

  const handleSetActive = async () => {
    try {
      if (setActiveType === "dynamicQr") {
        await NCHLCredentialsService.setDynamicQrCredsActive(setActiveItem.id);
        toast.success(
          "Dynamic QR credentials set as active. All other credentials will be disabled."
        );
        fetchDynamicQrCreds();
      } else if (setActiveType === "gateway") {
        await NCHLCredentialsService.setGatewayCredsActive(setActiveItem.id);
        toast.success(
          "Gateway credentials set as active. All other credentials will be disabled."
        );
        fetchGatewayCreds();
      }
      closeSetActiveModal();
    } catch (error) {
      toast.error("Failed to set credentials as active");
    }
  };

  const fetchDynamicQrCreds = async () => {
    try {
      setDynamicQrLoading(true);
      const res = await NCHLCredentialsService.getDynamicQrCredsByOrgId(id);
      setDynamicQrList(Array.isArray(res) ? res : [res]);
    } catch (error) {
      toast.error("Failed to fetch Dynamic QR credentials");
    } finally {
      setDynamicQrLoading(false);
    }
  };

  const fetchGatewayCreds = async () => {
    try {
      setGatewayLoading(true);
      const res = await NCHLCredentialsService.getGatewayCredsByOrgId(id);
      setGatewayList(Array.isArray(res) ? res : [res]);
    } catch (error) {
      toast.error("Failed to fetch Gateway credentials");
    } finally {
      setGatewayLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [id]);

  useEffect(() => {
    if (activeTab === "3") {
      fetchDynamicQrCreds();
    }
  }, [activeTab, id]);

  useEffect(() => {
    if (activeTab === "4") {
      fetchGatewayCreds();
    }
  }, [activeTab, id]);

  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
    }
  };

  // ============ Dynamic QR Handlers ============
  const handleDynamicQrInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setDynamicQrFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openDynamicQrModal = (item = null) => {
    if (item) {
      setEditingDynamicQr(item);
      setDynamicQrFormData(item);
    } else {
      setEditingDynamicQr(null);
      setDynamicQrFormData({
        organizationId: id,
        isUat: true,
        isLive: false,
        initilizationPoint: 0,
        merchantCode: "",
        acquirerId: "",
        merchantName: "",
        merchantCategoryCode: "",
        txnCurrencyCode: "",
        signerBasePath: "",
        signerPath: "",
        authUserName: "",
        basicUserName: "",
        authPassword: "",
        npiPfxPassword: "",
        dynamicQrUrl: "",
        publicKey: "",
        apiKey: "",
        webSocketUsername: "",
        webSocketUrl: "",
      });
    }
    setShowDynamicQrModal(true);
  };

  const closeDynamicQrModal = () => {
    setShowDynamicQrModal(false);
    setEditingDynamicQr(null);
  };

  const saveDynamicQrCreds = async () => {
    try {
      if (editingDynamicQr) {
        await NCHLCredentialsService.updateDynamicQrCreds(
          editingDynamicQr.id,
          dynamicQrFormData,
        );
        toast.success("Dynamic QR credentials updated successfully");
      } else {
        await NCHLCredentialsService.createDynamicQrCreds(dynamicQrFormData);
        toast.success("Dynamic QR credentials created successfully");
      }
      closeDynamicQrModal();
      fetchDynamicQrCreds();
    } catch (error) {
      toast.error("Failed to save Dynamic QR credentials");
    }
  };

  const deleteDynamicQrCreds = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?"))
      return;
    try {
      await NCHLCredentialsService.deleteDynamicQrCreds(id);
      toast.success("Dynamic QR credentials deleted successfully");
      fetchDynamicQrCreds();
    } catch (error) {
      toast.error("Failed to delete Dynamic QR credentials");
    }
  };

  // ============ Gateway Handlers ============
  const handleGatewayInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setGatewayFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const openGatewayModal = (item = null) => {
    if (item) {
      setEditingGateway(item);
      setGatewayFormData(item);
    } else {
      setEditingGateway(null);
      setGatewayFormData({
        organizationId: id,
      });
    }
    setShowGatewayModal(true);
  };

  const closeGatewayModal = () => {
    setShowGatewayModal(false);
    setEditingGateway(null);
  };

  const saveGatewayCreds = async () => {
    try {
      if (editingGateway) {
        await NCHLCredentialsService.updateGatewayCreds(
          editingGateway.id,
          gatewayFormData,
        );
        toast.success("Gateway credentials updated successfully");
      } else {
        await NCHLCredentialsService.createGatewayCreds(gatewayFormData);
        toast.success("Gateway credentials created successfully");
      }
      closeGatewayModal();
      fetchGatewayCreds();
    } catch (error) {
      toast.error("Failed to save Gateway credentials");
    }
  };

  const deleteGatewayCreds = async (id) => {
    if (!window.confirm("Are you sure you want to delete this credential?"))
      return;
    try {
      await NCHLCredentialsService.deleteGatewayCreds(id);
      toast.success("Gateway credentials deleted successfully");
      fetchGatewayCreds();
    } catch (error) {
      toast.error("Failed to delete Gateway credentials");
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5">
        <Spinner color="primary" /> Loading...
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center mt-5">
        <p>No tenant data available.</p>
      </div>
    );
  }

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb
          title="Tenant Detail"
          pageTitle="Tenant"
          pageLink="/tenant"
        />

        {/* ---------- HEADER CARD (same as customer detail) ---------- */}
        <Row>
          <Col lg={12}>
            <Card className="mt-n4 mx-n4 mb-2">
              <div className="bg-warning-subtle">
                <CardBody className="pb-0 px-4">
                  <Row className="align-items-center mb-2">
                    <Col md={8}>
                      <div>
                        {" "}
                        <h4 className="mb-1 text-capitalize">
                          {data.organizationName}
                        </h4>
                      </div>

                      <div className="hstack gap-3 flex-wrap text-muted ">
                        <div className="d-flex align-items-center justify-content-center">
                          <i className="ri-map-pin-line me-1"></i>
                          {data.address1}
                        </div>

                        <div className="vr"></div>

                        <div className="d-flex align-items-center justify-content-center">
                          <i className="ri-phone-line me-1"></i>
                          <a href={`tel:${data.tel}`}>{data.tel}</a>
                        </div>

                        <div className="vr"></div>

                        <div className="d-flex align-items-center justify-content-center">
                          <i className="ri-mail-line me-1"></i>
                          {data.email}
                        </div>
                      </div>
                    </Col>

                    {/* <Col md={4} className="text-md-end">
                      <div>
                        Created Date:{" "}
                        <span className="fw-medium">
                          {data.createdDate
                            ? new Date(data.createdDate).toLocaleString()
                            : "—"}
                        </span>
                      </div>
                      <div className="text-muted">
                        Created By: {data.createdBy}
                      </div>
                    </Col> */}
                  </Row>

                  {/* ---------- TABS (same pattern as customer) ---------- */}
                  <Nav
                    className="nav-tabs-custom border-bottom-0"
                    role="tablist"
                  >
                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "1" },
                          "fw-bold",
                        )}
                        onClick={() => toggleTab("1")}
                        href="#"
                      >
                        Organization Info
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "2" },
                          "fw-bold",
                        )}
                        onClick={() => toggleTab("2")}
                        href="#"
                      >
                        Database Info
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "3" },
                          "fw-bold",
                        )}
                        onClick={() => toggleTab("3")}
                        href="#"
                      >
                        NCHL Dynamic QR
                      </NavLink>
                    </NavItem>

                    <NavItem>
                      <NavLink
                        className={classnames(
                          { active: activeTab === "4" },
                          "fw-bold",
                        )}
                        onClick={() => toggleTab("4")}
                        href="#"
                      >
                        NCHL Gateway
                      </NavLink>
                    </NavItem>
                  </Nav>
                </CardBody>
              </div>
            </Card>
          </Col>
        </Row>

        {/* ---------- TAB CONTENT ---------- */}
        <Row>
          <Col lg={12}>
            <TabContent activeTab={activeTab} className="text-muted">
              <TabPane tabId="1">
                {activeTab === "1" && (
                  <Card>
                    <CardBody>
                      <SectionTitle title="Organization Information" />
                      <InfoRow
                        label="Organization Name"
                        value={data.organizationName}
                      />
                      <InfoRow label="Address" value={data.address1} />
                      <InfoRow label="Contact Number" value={data.tel} />
                      <InfoRow label="Email" value={data.email} />
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              <TabPane tabId="2">
                {activeTab === "2" && (
                  <Card>
                    <CardBody>
                      <SectionTitle title="Database Information" />
                      <InfoRow
                        label="Host Name"
                        value={data?.tenantInfo?.hostName}
                      />
                      <InfoRow
                        label="Server"
                        value={data?.tenantInfo?.server}
                      />
                      <InfoRow
                        label="Database Name"
                        value={data?.tenantInfo?.dbName}
                      />
                      <InfoRow
                        label="Database User"
                        value={data?.tenantInfo?.dbUser}
                      />
                      <InfoRow label="Database Password" value="********" />
                      <InfoRow
                        label="Admin Email"
                        value={data?.tenantInfo?.adminEmail}
                      />
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              <TabPane tabId="3">
                {activeTab === "3" && (
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <SectionTitle title="NCHL Dynamic QR Credentials" />
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => openDynamicQrModal()}
                        >
                          <i className="ri-add-line me-2"></i> Add New
                        </Button>
                      </div>

                      {dynamicQrLoading ? (
                        <div className="text-center">
                          <Spinner color="primary" size="sm" /> Loading...
                        </div>
                      ) : dynamicQrList.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>Merchant Code</th>
                                <th>Merchant Name</th>
                                <th>Acquirer ID</th>
                                <th>Category Code</th>
                                <th>Currency</th>
                                <th>Environment</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {dynamicQrList.map((item) => (
                                <tr key={item.id}>
                                  <td>
                                    <span className="">
                                      {item.merchantCode || "—"}
                                    </span>
                                  </td>
                                  <td>{item.merchantName || "—"}</td>
                                  <td>{item.acquirerId || "—"}</td>
                                  <td>
                                    <span className="">
                                      {item.merchantCategoryCode || "—"}
                                    </span>
                                  </td>
                                  <td>
                                    <span className="badge bg-info">
                                      {item.txnCurrencyCode || "—"}
                                    </span>
                                  </td>
                                  <td>
                                    {item.isUat && (
                                      <span className="badge bg-warning me-1">
                                        UAT
                                      </span>
                                    )}
                                    {item.isLive && (
                                      <span className="badge bg-success">
                                        LIVE
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <Button
                                      color="info"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => openDynamicQrModal(item)}
                                    >
                                      <i className="ri-edit-line"></i>
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      className="me-2"
                                      onClick={() =>
                                        openSetActiveModal(item, "dynamicQr")
                                      }
                                    >
                                      <i className="ri-check-line"></i>
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() =>
                                        deleteDynamicQrCreds(item.id)
                                      }
                                    >
                                      <i className="ri-delete-bin-line"></i>{" "}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <p>No NCHL Dynamic QR credentials found.</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}
              </TabPane>

              <TabPane tabId="4">
                {activeTab === "4" && (
                  <Card>
                    <CardBody>
                      <div className="d-flex justify-content-between align-items-center mb-4">
                        <SectionTitle title="NCHL Gateway Credentials" />
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => openGatewayModal()}
                        >
                          <i className="ri-add-line me-2"></i> Add New
                        </Button>
                      </div>

                      {gatewayLoading ? (
                        <div className="text-center">
                          <Spinner color="primary" size="sm" /> Loading...
                        </div>
                      ) : gatewayList.length > 0 ? (
                        <div className="table-responsive">
                          <table className="table table-hover">
                            <thead className="table-light">
                              <tr>
                                <th>App ID</th>
                                <th>App Name</th>
                                <th>Merchant ID</th>
                                <th>Currency</th>
                                <th>Gateway URL</th>
                                <th>Environment</th>
                                <th>Actions</th>
                              </tr>
                            </thead>
                            <tbody>
                              {gatewayList.map((item) => (
                                <tr key={item.id}>
                                  <td>
                                    <span className="">
                                      {item.appId || "—"}
                                    </span>
                                  </td>
                                  <td>{item.appName || "—"}</td>
                                  <td>
                                    <span className="">
                                      {item.merchantId || "—"}
                                    </span>
                                  </td>
                                  <td>{item.txnCrncy || "—"}</td>
                                  <td>
                                    {item.gatewayBaseUrl
                                      ? item.gatewayBaseUrl.substring(0, 30) +
                                        "..."
                                      : "—"}
                                  </td>
                                  <td>
                                    {item.isUat && (
                                      <span className="badge bg-warning me-1">
                                        UAT
                                      </span>
                                    )}
                                    {item.isLive && (
                                      <span className="badge bg-success">
                                        LIVE
                                      </span>
                                    )}
                                  </td>
                                  <td>
                                    <Button
                                      color="info"
                                      size="sm"
                                      className="me-2"
                                      onClick={() => openGatewayModal(item)}
                                    >
                                      <i className="ri-edit-line"></i>
                                    </Button>
                                    <Button
                                      color="warning"
                                      size="sm"
                                      className="me-2"
                                      onClick={() =>
                                        openSetActiveModal(item, "gateway")
                                      }
                                    >
                                      <i className="ri-check-line"></i>
                                    </Button>
                                    <Button
                                      color="danger"
                                      size="sm"
                                      onClick={() =>
                                        deleteGatewayCreds(item.id)
                                      }
                                    >
                                      <i className="ri-delete-bin-line"></i>{" "}
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="text-center text-muted py-4">
                          <p>No NCHL Gateway credentials found.</p>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}
              </TabPane>
            </TabContent>
          </Col>
        </Row>
      </Container>

      {/* ============ Dynamic QR Modal ============ */}
      <Modal isOpen={showDynamicQrModal} toggle={closeDynamicQrModal} size="lg">
        <ModalHeader toggle={closeDynamicQrModal}>
          {editingDynamicQr
            ? "Edit Dynamic QR Credentials"
            : "Add Dynamic QR Credentials"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="merchantCode">Merchant Code</Label>
                  <Input
                    type="text"
                    name="merchantCode"
                    id="merchantCode"
                    value={dynamicQrFormData.merchantCode}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter merchant code"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="merchantName">Merchant Name</Label>
                  <Input
                    type="text"
                    name="merchantName"
                    id="merchantName"
                    value={dynamicQrFormData.merchantName}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter merchant name"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="acquirerId">Acquirer ID</Label>
                  <Input
                    type="text"
                    name="acquirerId"
                    id="acquirerId"
                    value={dynamicQrFormData.acquirerId}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter acquirer ID"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="merchantCategoryCode">
                    Merchant Category Code
                  </Label>
                  <Input
                    type="text"
                    name="merchantCategoryCode"
                    id="merchantCategoryCode"
                    value={dynamicQrFormData.merchantCategoryCode}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter merchant category code"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="txnCurrencyCode">Transaction Currency Code</Label>
                  <Input
                    type="text"
                    name="txnCurrencyCode"
                    id="txnCurrencyCode"
                    value={dynamicQrFormData.txnCurrencyCode}
                    onChange={handleDynamicQrInputChange}
                    placeholder="e.g., NPR"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="initilizationPoint">Initialization Point</Label>
                  <Input
                    type="number"
                    name="initilizationPoint"
                    id="initilizationPoint"
                    value={dynamicQrFormData.initilizationPoint}
                    onChange={handleDynamicQrInputChange}
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="signerBasePath">Signer Base Path</Label>
                  <Input
                    type="text"
                    name="signerBasePath"
                    id="signerBasePath"
                    value={dynamicQrFormData.signerBasePath}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter signer base path"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="signerPath">Signer Path</Label>
                  <Input
                    type="text"
                    name="signerPath"
                    id="signerPath"
                    value={dynamicQrFormData.signerPath}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter signer path"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="authUserName">Auth Username</Label>
                  <Input
                    type="text"
                    name="authUserName"
                    id="authUserName"
                    value={dynamicQrFormData.authUserName}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter auth username"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="authPassword">Auth Password</Label>
                  <div className="position-relative">
                    <Input
                      type={showAuthPassword ? "text" : "password"}
                      name="authPassword"
                      id="authPassword"
                      value={dynamicQrFormData.authPassword}
                      onChange={handleDynamicQrInputChange}
                      placeholder="Enter auth password"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                      onClick={() => setShowAuthPassword(!showAuthPassword)}
                      style={{ padding: "0.5rem", marginTop: "0.5rem" }}
                    >
                      <i
                        className={
                          showAuthPassword ? "ri-eye-line" : "ri-eye-off-line"
                        }
                      ></i>
                    </button>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="basicUserName">Basic Username</Label>
                  <Input
                    type="text"
                    name="basicUserName"
                    id="basicUserName"
                    value={dynamicQrFormData.basicUserName}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter basic username"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="npiPfxPassword">NPI PFX Password</Label>
                  <div className="position-relative">
                    <Input
                      type={showNpiPassword ? "text" : "password"}
                      name="npiPfxPassword"
                      id="npiPfxPassword"
                      value={dynamicQrFormData.npiPfxPassword}
                      onChange={handleDynamicQrInputChange}
                      placeholder="Enter NPI PFX password"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                      onClick={() => setShowNpiPassword(!showNpiPassword)}
                      style={{ padding: "0.5rem", marginTop: "0.5rem" }}
                    >
                      <i
                        className={
                          showNpiPassword ? "ri-eye-line" : "ri-eye-off-line"
                        }
                      ></i>
                    </button>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="dynamicQrUrl">Dynamic QR URL</Label>
                  <Input
                    type="text"
                    name="dynamicQrUrl"
                    id="dynamicQrUrl"
                    value={dynamicQrFormData.dynamicQrUrl}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter dynamic QR URL"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="apiKey">API Key</Label>
                  <Input
                    type="text"
                    name="apiKey"
                    id="apiKey"
                    value={dynamicQrFormData.apiKey}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter API key"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="publicKey">Public Key</Label>
                  <Input
                    type="textarea"
                    name="publicKey"
                    id="publicKey"
                    value={dynamicQrFormData.publicKey}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter public key"
                    rows="3"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="webSocketUrl">WebSocket URL</Label>
                  <Input
                    type="text"
                    name="webSocketUrl"
                    id="webSocketUrl"
                    value={dynamicQrFormData.webSocketUrl}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter WebSocket URL"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="webSocketUsername">WebSocket Username</Label>
                  <Input
                    type="text"
                    name="webSocketUsername"
                    id="webSocketUsername"
                    value={dynamicQrFormData.webSocketUsername}
                    onChange={handleDynamicQrInputChange}
                    placeholder="Enter WebSocket username"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup check className="mt-4">
                  <Input
                    type="radio"
                    name="environment"
                    id="isUat"
                    value="uat"
                    checked={dynamicQrFormData.isUat}
                    onChange={(e) => {
                      setDynamicQrFormData({
                        ...dynamicQrFormData,
                        isUat: true,
                        isLive: false,
                      });
                    }}
                  />
                  <Label for="isUat" check>
                    Is UAT
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Input
                    type="radio"
                    name="environment"
                    id="isLive"
                    value="live"
                    checked={dynamicQrFormData.isLive}
                    onChange={(e) => {
                      setDynamicQrFormData({
                        ...dynamicQrFormData,
                        isUat: false,
                        isLive: true,
                      });
                    }}
                  />
                  <Label for="isLive" check>
                    Is Live
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            outline
            onClick={closeDynamicQrModal}
            className="bg-danger-soft"
          >
            Cancel
          </Button>
          <Button
            color="success"
            outline
            onClick={saveDynamicQrCreds}
            className="bg-success-soft"
          >
            {editingDynamicQr ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ============ Gateway Modal ============ */}
      <Modal isOpen={showGatewayModal} toggle={closeGatewayModal} size="lg">
        <ModalHeader toggle={closeGatewayModal}>
          {editingGateway
            ? "Edit Gateway Credentials"
            : "Add Gateway Credentials"}
        </ModalHeader>
        <ModalBody>
          <Form>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="merchantId">Merchant ID</Label>
                  <Input
                    type="text"
                    name="merchantId"
                    id="merchantId"
                    value={gatewayFormData.merchantId}
                    onChange={handleGatewayInputChange}
                    placeholder="Enter merchant ID"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="appId">App ID</Label>
                  <Input
                    type="text"
                    name="appId"
                    id="appId"
                    value={gatewayFormData.appId}
                    onChange={handleGatewayInputChange}
                    placeholder="Enter app ID"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="appName">App Name</Label>
                  <Input
                    type="text"
                    name="appName"
                    id="appName"
                    value={gatewayFormData.appName}
                    onChange={handleGatewayInputChange}
                    placeholder="Enter app name"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="txnCrncy">Transaction Currency</Label>
                  <Input
                    type="text"
                    name="txnCrncy"
                    id="txnCrncy"
                    value={gatewayFormData.txnCrncy}
                    onChange={handleGatewayInputChange}
                    placeholder="e.g., NPR"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="gatewyaBasicAuthPassword">
                    Gateway Basic Auth Password
                  </Label>
                  <div className="position-relative">
                    <Input
                      type={showGatewayAuthPassword ? "text" : "password"}
                      name="gatewyaBasicAuthPassword"
                      id="gatewyaBasicAuthPassword"
                      value={gatewayFormData.gatewyaBasicAuthPassword}
                      onChange={handleGatewayInputChange}
                      placeholder="Enter gateway basic auth password"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                      onClick={() =>
                        setShowGatewayAuthPassword(!showGatewayAuthPassword)
                      }
                      style={{ padding: "0.5rem", marginTop: "0.5rem" }}
                    >
                      <i
                        className={
                          showGatewayAuthPassword
                            ? "ri-eye-line"
                            : "ri-eye-off-line"
                        }
                      ></i>
                    </button>
                  </div>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="creditorPfxPassword">Creditor PFX Password</Label>
                  <div className="position-relative">
                    <Input
                      type={showGatewayCreditorPassword ? "text" : "password"}
                      name="creditorPfxPassword"
                      id="creditorPfxPassword"
                      value={gatewayFormData.creditorPfxPassword}
                      onChange={handleGatewayInputChange}
                      placeholder="Enter creditor PFX password"
                    />
                    <button
                      type="button"
                      className="btn btn-link position-absolute end-0 top-50 translate-middle-y"
                      onClick={() =>
                        setShowGatewayCreditorPassword(
                          !showGatewayCreditorPassword,
                        )
                      }
                      style={{ padding: "0.5rem", marginTop: "0.5rem" }}
                    >
                      <i
                        className={
                          showGatewayCreditorPassword
                            ? "ri-eye-line"
                            : "ri-eye-off-line"
                        }
                      ></i>
                    </button>
                  </div>
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="signerExternalLink">Signer External Link</Label>
                  <Input
                    type="text"
                    name="signerExternalLink"
                    id="signerExternalLink"
                    value={gatewayFormData.signerExternalLink}
                    onChange={handleGatewayInputChange}
                    placeholder="Enter signer external link"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="singerPath">Singer Path</Label>
                  <Input
                    type="text"
                    name="singerPath"
                    id="singerPath"
                    value={gatewayFormData.singerPath}
                    onChange={handleGatewayInputChange}
                    placeholder="Enter singer path"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={12}>
                <FormGroup>
                  <Label for="gatewayBaseUrl">Gateway Base URL</Label>
                  <Input
                    type="text"
                    name="gatewayBaseUrl"
                    id="gatewayBaseUrl"
                    value={gatewayFormData.gatewayBaseUrl}
                    onChange={handleGatewayInputChange}
                    placeholder="Enter gateway base URL"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup check className="mt-2">
                  <Input
                    type="radio"
                    name="gwEnvironment"
                    id="gwIsUat"
                    value="uat"
                    checked={gatewayFormData.isUat}
                    onChange={(e) => {
                      setGatewayFormData({
                        ...gatewayFormData,
                        isUat: true,
                        isLive: false,
                      });
                    }}
                  />
                  <Label for="gwIsUat" check>
                    Is UAT
                  </Label>
                </FormGroup>
                <FormGroup check>
                  <Input
                    type="radio"
                    name="gwEnvironment"
                    id="gwIsLive"
                    value="live"
                    checked={gatewayFormData.isLive}
                    onChange={(e) => {
                      setGatewayFormData({
                        ...gatewayFormData,
                        isUat: false,
                        isLive: true,
                      });
                    }}
                  />
                  <Label for="gwIsLive" check>
                    Is Live
                  </Label>
                </FormGroup>
              </Col>
            </Row>
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button
            color="danger"
            outline
            onClick={closeGatewayModal}
            className="bg-danger-soft"
          >
            Cancel
          </Button>
          <Button
            color="success"
            outline
            onClick={saveGatewayCreds}
            className="bg-success-soft"
          >
            {editingGateway ? "Update" : "Create"}
          </Button>
        </ModalFooter>
      </Modal>

      {/* ============ Set Active Modal ============ */}
      <Modal isOpen={showSetActiveModal} toggle={closeSetActiveModal}>
        <ModalHeader toggle={closeSetActiveModal} className="bg-warning-subtle">
          <i className="ri-alert-line me-2"></i> Confirm Set as Active
        </ModalHeader>
        <ModalBody className="py-4">
          <div className="alert alert-warning" role="alert">
            <h5 className="alert-heading">
              <i className="ri-alert-fill me-2"></i> Warning
            </h5>
            <p className="mb-0">
              This action will set the current{" "}
              <strong>
                {setActiveType === "dynamicQr"
                  ? "Dynamic QR credentials"
                  : "Gateway credentials"}
              </strong>{" "}
              as active and <strong>revert all other credentials to disabled</strong>.
            </p>
          </div>
          <div className="mt-3">
            {setActiveType === "dynamicQr" && setActiveItem && (
              <div>
                <p>
                  <strong>Merchant Code:</strong> {setActiveItem.merchantCode}
                </p>
                <p>
                  <strong>Merchant Name:</strong> {setActiveItem.merchantName}
                </p>
              </div>
            )}
            {setActiveType === "gateway" && setActiveItem && (
              <div>
                <p>
                  <strong>App ID:</strong> {setActiveItem.appId}
                </p>
                <p>
                  <strong>App Name:</strong> {setActiveItem.appName}
                </p>
              </div>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={closeSetActiveModal}
          >
            Cancel
          </Button>
          <Button
            color="warning"
            onClick={handleSetActive}
          >
            <i className="ri-check-line me-2"></i> Confirm & Set Active
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TenantDetail;
